import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Some platforms (observed in Electron/desktop-capture contexts) label the
 * getDisplayMedia video track with an internal capture identifier instead of
 * a human-readable tab/window title — e.g.
 * "web-contents-media-stream://ECED09DF072F272AF58F341DC8CC4F95" rather than
 * "Zoom Meeting - Google Chrome". These are always `<scheme>://<opaque-id>`
 * strings, so reject anything shaped like a URI rather than a title.
 */
const isInternalCaptureLabel = (label: string): boolean =>
  /^[a-z-]+:\/\//i.test(label);

/**
 * Share status of the telehealth call tab's audio.
 *  - `idle`    — nothing shared yet (or the user hasn't opened the picker).
 *  - `sharing` — a tab is shared and its audio track is live.
 *  - `lost`    — the shared tab stopped sharing (closed/switched away) while
 *                the session was still relying on it.
 */
export type CallAudioShareStatus = "idle" | "sharing" | "lost";

export type CallAudioShare = {
  status: CallAudioShareStatus;
  /** The shared tab's audio track, or null when not sharing. */
  track: MediaStreamTrack | null;
  /**
   * Human-readable label for the shared source (the browser's own tab/window
   * title, e.g. "Zoom Meeting - Google Chrome") — read from the picker's
   * video track, which the browser labels even though its video is never
   * displayed. Null when not sharing, or if the browser reports no label
   * (some platforms/permission states omit it).
   */
  sourceLabel: string | null;
  /**
   * Opens the browser's "choose what to share" picker. Callers must pick a
   * browser tab (not a window/screen) for audio to be captured — enforced by
   * `preferCurrentTab`-style browser UI, not by this hook. Resolves once a
   * track is live, or throws (e.g. user cancelled, unsupported browser).
   */
  share: () => Promise<MediaStreamTrack>;
  /** Stop sharing and release the track. */
  stop: () => void;
};

/**
 * A no-op share used before the root RecordingHost has registered the real
 * one in the store, so consumers can render without null-guards. `share()`
 * rejects — nothing can be shared until the real instance is registered.
 */
export const NOOP_CALL_AUDIO_SHARE: CallAudioShare = {
  status: "idle",
  track: null,
  sourceLabel: null,
  share: async () => {
    throw new Error("Call audio share is not ready yet.");
  },
  stop: () => {},
};

/**
 * Captures a shared browser tab's audio for telehealth Scribe sessions
 * (the "Everyone on the call" card in the consent modal). Tab-audio capture
 * requires `getDisplayMedia({ video: true, audio: true })` — video is part
 * of the picker contract even though only the audio track is kept. The video
 * track is never attached to anything (no video is ever shown), but it's
 * kept alive until real teardown — see the note in `share` below.
 *
 * Kept independent of useRecordingSession/Transcriber: this hook only owns
 * acquiring and watching the shared track. The consumer (Transcriber) mixes
 * it into the recording graph.
 */
export const useCallAudioShare = (): CallAudioShare => {
  const [status, setStatus] = useState<CallAudioShareStatus>("idle");
  const [track, setTrack] = useState<MediaStreamTrack | null>(null);
  const [sourceLabel, setSourceLabel] = useState<string | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  // The picker-only video track kept alive (never stopped) for as long as
  // the audio track it's tied to — see the comment in `share` below. Tracked
  // here so every real teardown (releaseTrack) stops both together instead
  // of leaking the browser's "sharing this tab" indicator.
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);

  const releaseTrack = useCallback(() => {
    trackRef.current?.stop();
    trackRef.current = null;
    videoTrackRef.current?.stop();
    videoTrackRef.current = null;
  }, []);

  const share = useCallback(async () => {
    releaseTrack();
    const displayStream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: true,
    });

    // Video is only required to satisfy getDisplayMedia's picker contract —
    // this feature never shows or records video. Do NOT stop the video track
    // here though: Chromium ties a tab-capture session's audio delivery to
    // the video track's lifecycle, so stopping it right after acquiring the
    // stream tears down the WHOLE capture session under the hood — the audio
    // track keeps reporting readyState "live" while silently delivering zero
    // samples, with no error anywhere. The unused video track is never
    // attached to anything (no <video> element, no AudioContext node) and is
    // stopped together with the audio track at every real teardown instead
    // (releaseTrack, above).
    const [videoTrack] = displayStream.getVideoTracks();
    const [audioTrack] = displayStream.getAudioTracks();
    if (!audioTrack) {
      videoTrack?.stop();
      throw new Error(
        "The shared tab has no audio. Choose the tab running the video call, not a window or screen."
      );
    }

    audioTrack.addEventListener("ended", () => {
      trackRef.current = null;
      setTrack(null);
      setSourceLabel(null);
      setStatus("lost");
      videoTrackRef.current?.stop();
      videoTrackRef.current = null;
    });

    trackRef.current = audioTrack;
    videoTrackRef.current = videoTrack ?? null;
    setTrack(audioTrack);
    // The video track is the one the browser labels with the shared tab's
    // title (e.g. "Zoom Meeting - Google Chrome") — the audio track's own
    // label is typically empty. Null (not "") when the browser gives no
    // label, OR an internal capture identifier instead of a real title, so
    // callers can fall back to a generic caption either way.
    const rawLabel = videoTrack?.label || "";
    setSourceLabel(rawLabel && !isInternalCaptureLabel(rawLabel) ? rawLabel : null);
    setStatus("sharing");
    return audioTrack;
  }, [releaseTrack]);

  const stop = useCallback(() => {
    releaseTrack();
    setTrack(null);
    setSourceLabel(null);
    setStatus("idle");
  }, [releaseTrack]);

  useEffect(() => releaseTrack, [releaseTrack]);

  return { status, track, sourceLabel, share, stop };
};
